import { FC, useEffect, useMemo, useRef, useState } from 'react';

import { router, usePage } from '@inertiajs/react';

import { BulkAutoPriceControls } from './BulkOfferMassEdit';
import { BulkOfferProductSearch } from './BulkOfferProductSearch';
import { BulkOffersQueue } from './BulkOffersQueue';
import { OfferImportModal } from './OfferImportModal';
import { useOfferDraftPersistence } from './useOfferDraftPersistence';
import cardmarketLogoUrl from '../../../../assets/auto-prices/cardmarket.jpg';
import tcgplayerLogoUrl from '../../../../assets/auto-prices/tcgplayer.jpg';
import { EmailConfirmationModal } from '../../../../components/Modals/EmailConfirmationModal';
import { Button } from '../../../../components/ui/Button';
import { Tabs, TabsList, TabsTrigger } from '../../../../components/ui/Tabs';
import { Routes } from '../../../../models/routes';
import {
    CartonProduct,
    CurrentUser,
    OfferDraft,
    OfferProperty,
    ProductType,
    Universe,
} from '../../../../types';
import {
    applyAutoPriceAdjustment,
    AutoPriceAdjustmentMode,
    buildDefaultPropertySet,
    createOfferDraftId,
    DEFAULT_AUTO_PRICE_MARKUP,
    EMPTY_OFFER_DRAFT_VALUES,
    hydrateOfferDraftDefaults,
    isOfferDraftValid,
    MAX_OFFER_DRAFTS_COUNT,
    normalizeOfferDraft,
    OfferDraftErrors,
    OfferDraftFormValues,
    resolveOfferPropertiesForProduct,
    validateOfferDraft,
} from '../__shared/offerDraft';
import { ProductContextSelectors } from '../__shared/ProductContextSelectors';

type PageProps = {
    offerProperties: OfferProperty[];
    initialProductOptions: CartonProduct[];
    preselectedProduct: CartonProduct | null;
    initialDrafts: OfferDraft[];
    initialEditingDraftId?: string | null;
    importBatchId?: number | null;
    initialDraftRevision?: number;
    initialUniverseId?: number | null;
    initialProductTypeId?: number | null;
    initialAutoPrice?: boolean;
    initialAutoPriceCurrency?: AutoPriceCurrency;
    initialAutoPriceMarkup?: number;
    initialAutoPriceAdjustmentMode?: AutoPriceAdjustmentMode;
    initialAutoPriceApplyToManual?: boolean;
    universes: Universe[];
    currentUser: CurrentUser;
};

type AutoPriceCurrency = 'EUR' | 'USD';
type AutoPriceResponse = {
    price: string;
    price_uah: number;
    price_amount: string;
    price_currency: AutoPriceCurrency;
    exchange_rate: string;
    price_source: string;
    scryfall_id?: string | null;
    cardmarket_product_id?: string | null;
    price_date?: string | null;
};
type AutoPriceErrorResponse = {
    error?: string;
    retryable?: boolean;
};

class AutoPriceRequestError extends Error {
    retryable: boolean;

    constructor(message: string, retryable: boolean) {
        super(message);
        this.name = 'AutoPriceRequestError';
        this.retryable = retryable;
    }
}

const MTG_UNIVERSE_SLUG = 'magic-the-gathering';
const RIFTBOUND_UNIVERSE_SLUG = 'riftbound-tcg';
const CARDMARKET_UNIVERSE_SLUGS = [
    'one-piece-tcg',
    'pokemon-tcg',
    'disney-lorcana',
    RIFTBOUND_UNIVERSE_SLUG,
    'star-wars-unlimited',
];
const AUTO_PRICE_REQUESTS_PER_SECOND = 10;
const AUTO_PRICE_CLIENT_DELAY_MS = 1000 / AUTO_PRICE_REQUESTS_PER_SECOND;
const AUTO_PRICE_MAX_RETRIES = 3;
const AUTO_PRICE_RETRY_DELAY_MS = 1000;

const AutoPriceSourceLogo: FC<{ src: string }> = ({ src }) => (
    <img
        src={src}
        alt=""
        aria-hidden="true"
        className="h-5 w-5 rounded-sm object-contain"
    />
);

export const NewOfferForm: FC = () => {
    const {
        offerProperties,
        initialProductOptions,
        preselectedProduct,
        initialDrafts,
        initialEditingDraftId,
        importBatchId,
        initialDraftRevision,
        initialUniverseId,
        initialProductTypeId,
        initialAutoPrice,
        initialAutoPriceCurrency,
        initialAutoPriceMarkup,
        initialAutoPriceAdjustmentMode,
        initialAutoPriceApplyToManual,
        universes,
        currentUser,
    } = usePage<PageProps>().props;

    const normalizedInitialDrafts = useMemo(() => {
        const hydratedDrafts = (initialDrafts ?? []).map((draft) =>
            hydrateOfferDraftDefaults(
                normalizeOfferDraft(draft),
                offerProperties,
            ),
        );

        if (hydratedDrafts.length > 0 || !preselectedProduct) {
            return hydratedDrafts;
        }

        return [
            normalizeOfferDraft({
                draft_id: createOfferDraftId(),
                product: preselectedProduct,
                product_id: preselectedProduct.id.toString(),
                price: EMPTY_OFFER_DRAFT_VALUES.price,
                amount: EMPTY_OFFER_DRAFT_VALUES.amount,
                property_set: buildDefaultPropertySet(
                    preselectedProduct,
                    offerProperties,
                ),
                universe_id: preselectedProduct.universe_id,
                product_type_id: preselectedProduct.product_type_id,
                source: 'manual',
                scryfall_id:
                    preselectedProduct.property_set?.scryfall_id ?? null,
            }),
        ];
    }, [initialDrafts, offerProperties, preselectedProduct]);
    const initialDraftToEditId = useMemo(
        () =>
            initialEditingDraftId ??
            ((initialDrafts ?? []).length > 0
                ? normalizedInitialDrafts[0]?.draft_id
                : null) ??
            null,
        [initialDrafts, initialEditingDraftId, normalizedInitialDrafts],
    );
    const initialDraftToEdit = useMemo(
        () =>
            normalizedInitialDrafts.find(
                (draft) => draft.draft_id === initialDraftToEditId,
            ) ?? null,
        [initialDraftToEditId, normalizedInitialDrafts],
    );

    const resolveProductTypesForUniverse = (
        universeId: number | null,
    ): ProductType[] =>
        universes.find((universe) => universe.id === universeId)
            ?.product_types ?? [];

    const initialSelectedProduct = initialDraftToEdit?.product ?? null;
    const initialSelectedUniverse =
        initialDraftToEdit?.product?.universe_id ??
        initialDraftToEdit?.universe_id ??
        preselectedProduct?.universe_id ??
        initialUniverseId ??
        universes?.[0]?.id ??
        null;
    const initialSelectedProductType =
        initialDraftToEdit?.product?.product_type_id ??
        initialDraftToEdit?.product_type_id ??
        preselectedProduct?.product_type_id ??
        initialProductTypeId ??
        null;
    const initialAutoFillPrices =
        Boolean(initialAutoPrice) &&
        universes.some(
            (universe) =>
                universe.id === initialSelectedUniverse &&
                universe.slug === MTG_UNIVERSE_SLUG,
        );

    const [showConfirmationModal, setShowConfirmationModal] = useState(false);
    const [showImportModal, setShowImportModal] = useState(false);
    const [queuedOffers, setQueuedOffers] = useState<OfferDraft[]>(
        normalizedInitialDrafts,
    );
    const queuedOffersRef = useRef<OfferDraft[]>(normalizedInitialDrafts);
    const bulkManualPriceRef = useRef('');
    const autoPriceQueueProcessingRef = useRef(false);
    const autoPriceQueueRunIdRef = useRef(0);
    const autoPriceProcessedDraftIdsRef = useRef<Set<string>>(new Set());
    const autoPriceRequestSlotRef = useRef<Promise<void>>(Promise.resolve());
    const [editingDraftId, setEditingDraftId] = useState<string | null>(
        initialDraftToEditId,
    );
    const editingDraftIdRef = useRef<string | null>(initialDraftToEditId);
    const [bulkProcessing, setBulkProcessing] = useState(false);
    const [bulkErrors, setBulkErrors] = useState<OfferDraftErrors>({});
    const [bulkPhotoUploadsInProgress, setBulkPhotoUploadsInProgress] =
        useState<Set<string>>(() => new Set());
    const [autoFillPrices, setAutoFillPrices] = useState(initialAutoFillPrices);
    const autoFillPricesRef = useRef(initialAutoFillPrices);
    const [autoPriceCurrency, setAutoPriceCurrency] =
        useState<AutoPriceCurrency>(initialAutoPriceCurrency ?? 'EUR');
    const autoPriceCurrencyRef = useRef<AutoPriceCurrency>(
        initialAutoPriceCurrency ?? 'EUR',
    );
    const [autoPriceMarkup, setAutoPriceMarkup] = useState(
        initialAutoPriceMarkup ?? DEFAULT_AUTO_PRICE_MARKUP,
    );
    const autoPriceMarkupRef = useRef(
        initialAutoPriceMarkup ?? DEFAULT_AUTO_PRICE_MARKUP,
    );
    const [autoPriceAdjustmentMode, setAutoPriceAdjustmentMode] =
        useState<AutoPriceAdjustmentMode>(
            initialAutoPriceAdjustmentMode ?? 'fixed',
        );
    const autoPriceAdjustmentModeRef = useRef<AutoPriceAdjustmentMode>(
        initialAutoPriceAdjustmentMode ?? 'fixed',
    );
    const [autoPriceApplyToManual, setAutoPriceApplyToManual] = useState(
        initialAutoPriceApplyToManual ?? false,
    );
    const autoPriceApplyToManualRef = useRef(
        initialAutoPriceApplyToManual ?? false,
    );
    const [autoPriceExchangeRate, setAutoPriceExchangeRate] = useState<
        string | null
    >(null);
    const [selectedProduct, setSelectedProduct] =
        useState<CartonProduct | null>(initialSelectedProduct ?? null);
    const [selectedUniverseId, setSelectedUniverseId] = useState<number | null>(
        initialSelectedUniverse,
    );
    const [selectedProductTypeId, setSelectedProductTypeId] = useState<
        number | null
    >(initialSelectedProductType);
    const {
        batchId: draftBatchId,
        status: draftSaveStatus,
        flush: flushDrafts,
        pause: pauseDraftSaving,
        resume: resumeDraftSaving,
    } = useOfferDraftPersistence({
        drafts: queuedOffers,
        editingDraftId,
        universeId: selectedUniverseId,
        autoPriceMarkup,
        autoPriceAdjustmentMode,
        autoPriceApplyToManual,
        initialBatchId: importBatchId,
        initialRevision: initialDraftRevision,
    });

    const productTypes = resolveProductTypesForUniverse(selectedUniverseId);
    const properties = resolveOfferPropertiesForProduct(
        selectedProduct,
        offerProperties,
    );

    useEffect(() => {
        queuedOffersRef.current = queuedOffers;
    }, [queuedOffers]);

    useEffect(() => {
        editingDraftIdRef.current = editingDraftId;
    }, [editingDraftId]);

    useEffect(() => {
        autoFillPricesRef.current = autoFillPrices;
    }, [autoFillPrices]);

    useEffect(() => {
        autoPriceCurrencyRef.current = autoPriceCurrency;
    }, [autoPriceCurrency]);

    const isMtgUniverseId = (universeId?: number | null) =>
        universes.some(
            (universe) =>
                universe.id === universeId &&
                universe.slug === MTG_UNIVERSE_SLUG,
        );

    const isCardmarketUniverseId = (universeId?: number | null) =>
        universes.some(
            (universe) =>
                universe.id === universeId &&
                CARDMARKET_UNIVERSE_SLUGS.includes(universe.slug),
        );

    const isAutoPriceUniverseId = (universeId?: number | null) =>
        isMtgUniverseId(universeId) || isCardmarketUniverseId(universeId);

    const isMtgProduct = (product: CartonProduct | null) =>
        Boolean(product?.universe_id && isMtgUniverseId(product.universe_id));

    const isRiftboundProduct = (product: CartonProduct | null) =>
        Boolean(
            product?.universe_id &&
                universes.some(
                    (universe) =>
                        universe.id === product.universe_id &&
                        universe.slug === RIFTBOUND_UNIVERSE_SLUG,
                ),
        );

    const isAutoPriceProduct = (product: CartonProduct | null) =>
        Boolean(
            product?.universe_id && isAutoPriceUniverseId(product.universe_id),
        );

    const priceVariantFromPropertySet = (
        product: CartonProduct | null,
        propertySet: Record<string, string>,
    ) => {
        if (isMtgProduct(product)) {
            return `version:${propertySet.version?.trim() || ''}`;
        }

        if (isRiftboundProduct(product)) {
            return `foil:${propertySet.foil?.trim() || 'false'}`;
        }

        return null;
    };

    const hasEmptyDraftPrice = (draft: OfferDraft) => {
        const price = draft.price?.toString().trim();

        return (
            !price || price === '0' || price === EMPTY_OFFER_DRAFT_VALUES.price
        );
    };

    const draftScryfallId = (draft: OfferDraft) =>
        draft.scryfall_id?.trim() ||
        draft.product?.property_set?.scryfall_id?.trim() ||
        null;

    const canQueueDraftForAutoPrice = (draft: OfferDraft, force = false) =>
        Boolean(
            draft.product &&
                isAutoPriceProduct(draft.product) &&
                !draft.import_error &&
                draft.auto_price_status !== 'manual' &&
                (force || hasEmptyDraftPrice(draft)),
        );

    const queueDraftsForAutoPrice = (drafts: OfferDraft[], force = false) =>
        drafts.map((draft) => {
            if (!canQueueDraftForAutoPrice(draft, force)) return draft;

            const scryfallId = draftScryfallId(draft);
            if (isMtgProduct(draft.product) && !scryfallId) {
                return normalizeOfferDraft({
                    ...draft,
                    auto_price_status: 'unavailable',
                    auto_price_error: 'Scryfall ID відсутній для цього товару',
                });
            }

            return normalizeOfferDraft({
                ...draft,
                scryfall_id: scryfallId,
                auto_price_status: 'pending',
                auto_price_error: null,
            });
        });

    const clearQueuedAutoPriceStatuses = (drafts: OfferDraft[]) =>
        drafts.map((draft) => {
            if (
                draft.auto_price_status !== 'pending' &&
                draft.auto_price_status !== 'loading'
            ) {
                return draft;
            }

            return normalizeOfferDraft({
                ...draft,
                auto_price_status: null,
                auto_price_error: null,
            });
        });

    const autoPriceErrorMessage = (error: unknown) =>
        error instanceof Error ? error.message : 'Не вдалося отримати автоціну';

    const adjustedAutoPrice = (basePrice: string) =>
        applyAutoPriceAdjustment(
            basePrice,
            autoPriceMarkupRef.current,
            autoPriceAdjustmentModeRef.current,
        );

    const fetchAutoPrice = async (
        product: CartonProduct,
        propertySet: Record<string, string>,
        currency = autoPriceCurrencyRef.current,
    ): Promise<AutoPriceResponse> => {
        const queryParams = new URLSearchParams({
            product_id: product.id.toString(),
            auto_price_currency: currency,
        });

        if (isMtgProduct(product)) {
            const version = propertySet.version?.trim();
            if (version) {
                queryParams.set('version', version);
            }
        }

        if (isRiftboundProduct(product)) {
            const foil = propertySet.foil?.trim();
            if (foil) {
                queryParams.set('foil', foil);
            }
        }

        let response: Response;
        try {
            response = await fetch(
                `${Routes.AutoPriceOffer}?${queryParams.toString()}`,
                {
                    headers: { Accept: 'application/json' },
                },
            );
        } catch {
            throw new AutoPriceRequestError(
                'Не вдалося з’єднатися для отримання автоціни',
                true,
            );
        }

        const payload = (await response
            .json()
            .catch(() => ({}))) as AutoPriceResponse & AutoPriceErrorResponse;

        if (!response.ok) {
            throw new AutoPriceRequestError(
                payload.error ?? 'Не вдалося отримати автоціну',
                payload.retryable === true ||
                    response.status === 429 ||
                    response.status >= 500,
            );
        }

        if (payload.exchange_rate) {
            setAutoPriceExchangeRate(payload.exchange_rate);
        }

        return payload as AutoPriceResponse;
    };

    const updateQueuedDraftAutoPrice = (
        draftId: string,
        attributes: Partial<OfferDraft>,
    ) => {
        setQueuedOffers((currentDrafts) =>
            currentDrafts.map((draft) =>
                draft.draft_id === draftId
                    ? normalizeOfferDraft({ ...draft, ...attributes })
                    : draft,
            ),
        );
    };

    const waitForAutoPriceDelay = (delayMs: number) =>
        new Promise<void>((resolve) => {
            window.setTimeout(resolve, delayMs);
        });

    const scheduleAutoPriceRequest = async <Result,>(
        request: () => Promise<Result>,
    ): Promise<Result> => {
        const requestSlot = autoPriceRequestSlotRef.current;
        autoPriceRequestSlotRef.current = requestSlot.then(() =>
            waitForAutoPriceDelay(AUTO_PRICE_CLIENT_DELAY_MS),
        );

        await requestSlot;
        return request();
    };

    const pendingAutoPriceDrafts = (drafts: OfferDraft[]) =>
        drafts.filter(
            (draft) =>
                draft.auto_price_status === 'pending' &&
                draft.product &&
                !draft.import_error &&
                draft.draft_id !== editingDraftIdRef.current &&
                !autoPriceProcessedDraftIdsRef.current.has(draft.draft_id),
        );

    const isCurrentAutoPriceQueueRun = (runId: number) =>
        autoFillPricesRef.current && autoPriceQueueRunIdRef.current === runId;

    const fetchAutoPriceWithRetry = async (
        draft: OfferDraft,
        runId: number,
    ): Promise<AutoPriceResponse> => {
        const product = draft.product;
        if (!product) {
            throw new AutoPriceRequestError('Товар не знайдено', false);
        }

        let retryCount = 0;

        while (true) {
            try {
                return await scheduleAutoPriceRequest(() => {
                    if (!isCurrentAutoPriceQueueRun(runId)) {
                        throw new AutoPriceRequestError(
                            'Чергу автоцін зупинено',
                            false,
                        );
                    }

                    return fetchAutoPrice(product, draft.property_set);
                });
            } catch (error) {
                const retryable =
                    error instanceof AutoPriceRequestError && error.retryable;
                if (
                    !isCurrentAutoPriceQueueRun(runId) ||
                    !retryable ||
                    retryCount >= AUTO_PRICE_MAX_RETRIES
                ) {
                    throw error;
                }

                const retryDelay = AUTO_PRICE_RETRY_DELAY_MS * 2 ** retryCount;
                retryCount += 1;
                await waitForAutoPriceDelay(retryDelay);
            }
        }
    };

    const processQueuedAutoPriceDraft = async (
        draft: OfferDraft,
        runId: number,
    ) => {
        if (!draft.product) return;

        autoPriceProcessedDraftIdsRef.current.add(draft.draft_id);
        updateQueuedDraftAutoPrice(draft.draft_id, {
            auto_price_status: 'loading',
            auto_price_error: null,
        });

        try {
            const payload = await fetchAutoPriceWithRetry(draft, runId);
            if (!isCurrentAutoPriceQueueRun(runId)) return;

            updateQueuedDraftAutoPrice(draft.draft_id, {
                price: adjustedAutoPrice(payload.price),
                auto_price_base: payload.price,
                scryfall_id: payload.scryfall_id ?? draft.scryfall_id,
                auto_price_status: 'priced',
                auto_price_error: null,
                auto_price_source: payload.price_source,
            });
        } catch (error) {
            if (!isCurrentAutoPriceQueueRun(runId)) return;

            updateQueuedDraftAutoPrice(draft.draft_id, {
                auto_price_status: 'error',
                auto_price_error: autoPriceErrorMessage(error),
            });
        }
    };

    const startQueuedAutoPriceFetch = (drafts: OfferDraft[]) => {
        if (autoPriceQueueProcessingRef.current) return;
        if (!autoFillPricesRef.current) return;

        const draftsToProcess = pendingAutoPriceDrafts(drafts);
        if (draftsToProcess.length === 0) return;

        autoPriceQueueProcessingRef.current = true;
        const runId = autoPriceQueueRunIdRef.current;

        void (async () => {
            await Promise.all(
                draftsToProcess.map((draft) =>
                    processQueuedAutoPriceDraft(draft, runId),
                ),
            );

            autoPriceQueueProcessingRef.current = false;
            if (autoFillPricesRef.current) {
                startQueuedAutoPriceFetch(queuedOffersRef.current);
            }
        })();
    };

    const disableAutoPrices = () => {
        autoPriceQueueRunIdRef.current += 1;
        autoFillPricesRef.current = false;
        setAutoFillPrices(false);

        const nextQueuedOffers = clearQueuedAutoPriceStatuses(
            queuedOffersRef.current,
        );
        queuedOffersRef.current = nextQueuedOffers;
        setQueuedOffers(nextQueuedOffers);
    };

    const handleAutoPriceToggle = (checked = !autoFillPrices) => {
        if (!checked) {
            disableAutoPrices();
            return;
        }

        if (!isAutoPriceUniverseId(selectedUniverseId)) return;

        const nextCurrency: AutoPriceCurrency = isMtgUniverseId(
            selectedUniverseId,
        )
            ? autoPriceCurrencyRef.current
            : 'EUR';
        autoPriceCurrencyRef.current = nextCurrency;
        setAutoPriceCurrency(nextCurrency);

        autoPriceQueueRunIdRef.current += 1;
        autoFillPricesRef.current = true;
        setAutoFillPrices(true);

        autoPriceProcessedDraftIdsRef.current.clear();
        const nextQueuedOffers = queueDraftsForAutoPrice(
            queuedOffersRef.current,
        );
        queuedOffersRef.current = nextQueuedOffers;
        setQueuedOffers(nextQueuedOffers);
        startQueuedAutoPriceFetch(nextQueuedOffers);
    };

    const handleAutoPriceCurrencyChange = (currency: AutoPriceCurrency) => {
        if (currency === autoPriceCurrencyRef.current) return;

        autoPriceCurrencyRef.current = currency;
        setAutoPriceCurrency(currency);

        if (
            !autoFillPricesRef.current ||
            !isMtgUniverseId(selectedUniverseId)
        ) {
            return;
        }

        autoPriceQueueRunIdRef.current += 1;
        autoPriceProcessedDraftIdsRef.current.clear();

        const nextQueuedOffers = queueDraftsForAutoPrice(
            queuedOffersRef.current,
            true,
        );
        queuedOffersRef.current = nextQueuedOffers;
        setQueuedOffers(nextQueuedOffers);
        startQueuedAutoPriceFetch(nextQueuedOffers);
    };

    const repriceAutoPriceDrafts = () => {
        const nextQueuedOffers = queuedOffersRef.current.map((draft) => {
            const shouldReprice =
                draft.auto_price_status === 'priced' ||
                (autoPriceApplyToManualRef.current &&
                    draft.auto_price_status === 'manual');
            if (!shouldReprice || !draft.auto_price_base) return draft;

            const basePrice = draft.auto_price_base;

            return normalizeOfferDraft({
                ...draft,
                auto_price_base: basePrice,
                price: adjustedAutoPrice(basePrice),
            });
        });

        queuedOffersRef.current = nextQueuedOffers;
        setQueuedOffers(nextQueuedOffers);
    };

    const handleAutoPriceMarkupChange = (markup: number) => {
        autoPriceMarkupRef.current = markup;
        setAutoPriceMarkup(markup);
        repriceAutoPriceDrafts();
    };

    const handleAutoPriceAdjustmentModeChange = (
        mode: AutoPriceAdjustmentMode,
    ) => {
        autoPriceAdjustmentModeRef.current = mode;
        setAutoPriceAdjustmentMode(mode);
        repriceAutoPriceDrafts();
    };

    const handleAutoPriceApplyToManualChange = (apply: boolean) => {
        autoPriceApplyToManualRef.current = apply;
        setAutoPriceApplyToManual(apply);
        if (apply) repriceAutoPriceDrafts();
    };

    const retryFailedAutoPrices = () => {
        const failedDraftIds = new Set(
            queuedOffersRef.current
                .filter((draft) => draft.auto_price_status === 'error')
                .map((draft) => draft.draft_id),
        );
        if (failedDraftIds.size === 0) return;

        failedDraftIds.forEach((draftId) =>
            autoPriceProcessedDraftIdsRef.current.delete(draftId),
        );
        const nextQueuedOffers = queuedOffersRef.current.map((draft) =>
            failedDraftIds.has(draft.draft_id)
                ? queueDraftsForAutoPrice([draft], true)[0]
                : draft,
        );

        queuedOffersRef.current = nextQueuedOffers;
        setQueuedOffers(nextQueuedOffers);
        startQueuedAutoPriceFetch(nextQueuedOffers);
    };

    useEffect(() => {
        if (!autoFillPrices || isAutoPriceUniverseId(selectedUniverseId))
            return;

        disableAutoPrices();
    }, [autoFillPrices, selectedUniverseId]);

    useEffect(() => {
        if (!autoFillPrices) return;

        startQueuedAutoPriceFetch(queuedOffersRef.current);
    }, [autoFillPrices, editingDraftId]);

    useEffect(() => {
        if (normalizedInitialDrafts.length === 0 && !initialEditingDraftId) {
            return;
        }

        const nextInitialDrafts = autoFillPrices
            ? queueDraftsForAutoPrice(normalizedInitialDrafts)
            : normalizedInitialDrafts;

        setQueuedOffers(nextInitialDrafts);
        if (autoFillPrices) {
            startQueuedAutoPriceFetch(nextInitialDrafts);
        }

        const contextDraft = initialDraftToEdit ?? nextInitialDrafts[0];

        if (contextDraft) {
            setSelectedUniverseId(
                contextDraft.product?.universe_id ??
                    contextDraft.universe_id ??
                    null,
            );
            setSelectedProductTypeId(
                contextDraft.product?.product_type_id ??
                    contextDraft.product_type_id ??
                    null,
            );
        }

        const draftNeedingProduct =
            initialDraftToEdit && !initialDraftToEdit.product
                ? initialDraftToEdit
                : nextInitialDrafts.find((draft) => !draft.product);

        setSelectedProduct(null);
        setEditingDraftId(draftNeedingProduct?.draft_id ?? null);
    }, [initialDraftToEdit, initialEditingDraftId, normalizedInitialDrafts]);

    const clearSelectedProduct = () => {
        setSelectedProduct(null);
    };

    const resetEditor = () => {
        setEditingDraftId(null);
        setBulkErrors({});
        setSelectedProduct(null);
    };

    const handleUniverseChange = (universeId: number) => {
        setSelectedUniverseId(universeId);
        setSelectedProductTypeId(null);
        clearSelectedProduct();
        setBulkErrors((previousErrors) => {
            if (!previousErrors.product_id && !previousErrors.property_set) {
                return previousErrors;
            }

            const nextErrors = { ...previousErrors };
            delete nextErrors.product_id;
            delete nextErrors.property_set;
            return nextErrors;
        });
    };

    const handleProductTypeChange = (productTypeId: number) => {
        setSelectedProductTypeId(productTypeId);
        clearSelectedProduct();
        setBulkErrors((previousErrors) => {
            if (!previousErrors.product_id && !previousErrors.property_set) {
                return previousErrors;
            }

            const nextErrors = { ...previousErrors };
            delete nextErrors.product_id;
            delete nextErrors.property_set;
            return nextErrors;
        });
    };

    const clearBulkFieldError = (field: keyof OfferDraftFormValues) => {
        setBulkErrors((previousErrors) => {
            if (!previousErrors[field]) return previousErrors;

            const nextErrors = { ...previousErrors };
            delete nextErrors[field];
            return nextErrors;
        });
    };

    const commitQueuedOffers = (nextQueuedOffers: OfferDraft[]) => {
        queuedOffersRef.current = nextQueuedOffers;
        setQueuedOffers(nextQueuedOffers);

        if (autoFillPricesRef.current) {
            startQueuedAutoPriceFetch(nextQueuedOffers);
        }

        return nextQueuedOffers;
    };

    const propertySetForProduct = (
        product: CartonProduct,
        propertySet?: Record<string, string>,
    ) => {
        const supportedSlugs = new Set(
            resolveOfferPropertiesForProduct(product, offerProperties).map(
                (property) => property.slug,
            ),
        );
        const carriedValues = Object.entries(propertySet ?? {}).filter(
            ([slug]) => supportedSlugs.has(slug),
        );

        return {
            ...buildDefaultPropertySet(product, offerProperties),
            ...Object.fromEntries(carriedValues),
        };
    };

    const withQueuedAutoPrice = (draft: OfferDraft) =>
        autoFillPricesRef.current ? queueDraftsForAutoPrice([draft])[0] : draft;

    const withPropertySetUpdate = (
        draft: OfferDraft,
        nextPropertySet: Record<string, string>,
    ) => {
        const shouldRefreshAutoPrice =
            autoFillPricesRef.current &&
            draft.product &&
            draft.auto_price_status !== 'manual' &&
            priceVariantFromPropertySet(draft.product, draft.property_set) !==
                priceVariantFromPropertySet(draft.product, nextPropertySet);
        const updatedDraft = normalizeOfferDraft({
            ...draft,
            property_set: nextPropertySet,
        });

        if (!shouldRefreshAutoPrice) return updatedDraft;

        autoPriceProcessedDraftIdsRef.current.delete(draft.draft_id);

        return queueDraftsForAutoPrice(
            [
                normalizeOfferDraft({
                    ...updatedDraft,
                    price: '0',
                    auto_price_status: null,
                    auto_price_error: null,
                    auto_price_base: null,
                }),
            ],
            true,
        )[0];
    };

    const findNextDraftNeedingProduct = (
        drafts: OfferDraft[],
        currentDraftId: string | null,
    ) => {
        const currentDraftIndex = drafts.findIndex(
            (draft) => draft.draft_id === currentDraftId,
        );
        const orderedDrafts =
            currentDraftIndex === -1
                ? drafts
                : [
                      ...drafts.slice(currentDraftIndex + 1),
                      ...drafts.slice(0, currentDraftIndex),
                  ];

        return orderedDrafts.find((draft) => !draft.product) ?? null;
    };

    const startEditingDraft = (draftId: string) => {
        const draft = queuedOffersRef.current.find(
            (queuedOffer) => queuedOffer.draft_id === draftId,
        );

        if (!draft) return;

        setEditingDraftId(draftId);
        setSelectedProduct(draft.product);
        setSelectedUniverseId(
            draft.product?.universe_id ?? draft.universe_id ?? null,
        );
        setSelectedProductTypeId(
            draft.product?.product_type_id ?? draft.product_type_id ?? null,
        );
        setBulkErrors({});
    };

    const addDraftForProduct = (product: CartonProduct) => {
        if (queuedOffersRef.current.length >= MAX_OFFER_DRAFTS_COUNT) {
            setBulkErrors({
                product_id: `У черзі може бути не більше ${MAX_OFFER_DRAFTS_COUNT} рядків`,
            });
            return;
        }

        const newDraft = normalizeOfferDraft({
            draft_id: createOfferDraftId(),
            product,
            product_id: product.id.toString(),
            price: bulkManualPriceRef.current || EMPTY_OFFER_DRAFT_VALUES.price,
            amount: EMPTY_OFFER_DRAFT_VALUES.amount,
            property_set: propertySetForProduct(product),
            universe_id: product.universe_id,
            product_type_id: product.product_type_id,
            source: 'manual',
            scryfall_id: product.property_set?.scryfall_id ?? null,
        });

        commitQueuedOffers([
            withQueuedAutoPrice(newDraft),
            ...queuedOffersRef.current,
        ]);
    };

    const replaceDraftProduct = (draftId: string, product: CartonProduct) => {
        const currentDraft = queuedOffersRef.current.find(
            (draft) => draft.draft_id === draftId,
        );

        if (!currentDraft) return;

        autoPriceProcessedDraftIdsRef.current.delete(draftId);

        const updatedDraft = normalizeOfferDraft({
            ...currentDraft,
            product,
            product_id: product.id.toString(),
            property_set: propertySetForProduct(
                product,
                currentDraft.property_set,
            ),
            universe_id: product.universe_id ?? currentDraft.universe_id,
            product_type_id:
                product.product_type_id ?? currentDraft.product_type_id,
            import_error: null,
            scryfall_id: product.property_set?.scryfall_id ?? null,
            auto_price_status:
                currentDraft.auto_price_status === 'manual' ? 'manual' : null,
            auto_price_error: null,
            auto_price_base: null,
        });
        const nextQueuedOffers = commitQueuedOffers(
            queuedOffersRef.current.map((draft) =>
                draft.draft_id === draftId
                    ? withQueuedAutoPrice(updatedDraft)
                    : draft,
            ),
        );
        const nextDraftNeedingProduct = findNextDraftNeedingProduct(
            nextQueuedOffers,
            draftId,
        );

        if (nextDraftNeedingProduct) {
            startEditingDraft(nextDraftNeedingProduct.draft_id);
            return;
        }

        resetEditor();
    };

    const handleBulkProductSelected = (product: CartonProduct) => {
        clearBulkFieldError('product_id');
        clearBulkFieldError('property_set');

        if (product.universe_id) {
            setSelectedUniverseId(product.universe_id);
        }

        if (product.product_type_id) {
            setSelectedProductTypeId(product.product_type_id);
        }

        if (editingDraftId) {
            replaceDraftProduct(editingDraftId, product);
            return;
        }

        addDraftForProduct(product);
        setSelectedProduct(null);
    };

    const removeDrafts = (draftIds: string[]) => {
        const removedIds = new Set(draftIds);

        commitQueuedOffers(
            queuedOffersRef.current.filter(
                (draft) => !removedIds.has(draft.draft_id),
            ),
        );

        if (editingDraftId && removedIds.has(editingDraftId)) {
            resetEditor();
        }

        setBulkPhotoUploadsInProgress((currentIds) => {
            const nextIds = new Set(currentIds);
            removedIds.forEach((draftId) => nextIds.delete(draftId));
            return nextIds;
        });
    };

    const removeDraft = (draftId: string) => removeDrafts([draftId]);

    const duplicateDraft = (draftId: string) => {
        if (queuedOffersRef.current.length >= MAX_OFFER_DRAFTS_COUNT) {
            setBulkErrors({
                product_id: `У черзі може бути не більше ${MAX_OFFER_DRAFTS_COUNT} рядків`,
            });
            return;
        }

        const sourceDraft = queuedOffersRef.current.find(
            (draft) => draft.draft_id === draftId,
        );
        if (!sourceDraft) return;

        const duplicatedDraft = normalizeOfferDraft({
            ...sourceDraft,
            draft_id: createOfferDraftId(),
            source: 'manual',
            source_code: null,
            import_name: null,
            import_error: null,
            auto_price_status:
                sourceDraft.auto_price_status === 'loading'
                    ? 'pending'
                    : sourceDraft.auto_price_status,
        });

        commitQueuedOffers([...queuedOffersRef.current, duplicatedDraft]);
    };

    const updateQueuedDraftField = <Key extends keyof OfferDraftFormValues>(
        draftId: string,
        field: Key,
        value: OfferDraftFormValues[Key],
    ) => {
        const currentDraft = queuedOffersRef.current.find(
            (draft) => draft.draft_id === draftId,
        );
        if (!currentDraft) return;

        let updatedDraft: OfferDraft;

        if (field === 'property_set') {
            updatedDraft = withPropertySetUpdate(
                currentDraft,
                value as Record<string, string>,
            );
        } else {
            updatedDraft = normalizeOfferDraft({
                ...currentDraft,
                [field]: value,
            });

            if (field === 'price') {
                autoPriceProcessedDraftIdsRef.current.add(draftId);
                updatedDraft = normalizeOfferDraft({
                    ...updatedDraft,
                    auto_price_status: 'manual',
                    auto_price_error: null,
                });
            }
        }

        commitQueuedOffers(
            queuedOffersRef.current.map((draft) =>
                draft.draft_id === draftId ? updatedDraft : draft,
            ),
        );
    };

    const handleBulkPhotoUploadingChange = (
        draftId: string,
        uploading: boolean,
    ) => {
        setBulkPhotoUploadsInProgress((currentIds) => {
            const nextIds = new Set(currentIds);

            if (uploading) {
                nextIds.add(draftId);
            } else {
                nextIds.delete(draftId);
            }

            return nextIds;
        });
    };

    const applyPropertyToDrafts = (
        draftIds: string[],
        slug: string,
        value: string,
    ) => {
        const targetIds = new Set(draftIds);

        commitQueuedOffers(
            queuedOffersRef.current.map((draft) => {
                if (!targetIds.has(draft.draft_id)) return draft;

                const matchingProperty = resolveOfferPropertiesForProduct(
                    draft.product,
                    offerProperties,
                ).find((property) => property.slug === slug);
                const isSupportedValue =
                    matchingProperty?.kind === 'string' ||
                    (matchingProperty?.kind === 'collection' &&
                        matchingProperty.offer_property_options.some(
                            (option) => option.slug === value,
                        ));

                if (!isSupportedValue) return draft;

                return withPropertySetUpdate(draft, {
                    ...draft.property_set,
                    [slug]: value,
                });
            }),
        );
    };

    const applyPriceToDrafts = (draftIds: string[], price: string) => {
        const targetIds = new Set(draftIds);

        commitQueuedOffers(
            queuedOffersRef.current.map((draft) => {
                if (!targetIds.has(draft.draft_id)) return draft;

                autoPriceProcessedDraftIdsRef.current.add(draft.draft_id);

                return normalizeOfferDraft({
                    ...draft,
                    price,
                    auto_price_status: 'manual',
                    auto_price_error: null,
                });
            }),
        );
    };

    const updateBulkManualPrice = (price: string) => {
        bulkManualPriceRef.current = price;
    };

    const submitBulkOffers = async () => {
        if (bulkPhotoUploadsInProgress.size > 0) return;

        if (!currentUser.email_confirmed) {
            setShowConfirmationModal(true);
            return;
        }

        const nextQueuedOffers = queuedOffers;
        const invalidDraft = nextQueuedOffers.find(
            (draft) => !isOfferDraftValid(draft, offerProperties),
        );

        if (invalidDraft) {
            setBulkErrors(validateOfferDraft(invalidDraft, offerProperties));

            if (!invalidDraft.product) {
                startEditingDraft(invalidDraft.draft_id);
            }

            return;
        }

        if (nextQueuedOffers.length === 0) return;

        setBulkProcessing(true);
        setBulkErrors({});
        const persistedBatchId = await flushDrafts();
        pauseDraftSaving();
        let createdSuccessfully = false;

        router.post(
            Routes.BulkCreateOffers,
            {
                import_batch_id: persistedBatchId,
                offers: nextQueuedOffers.map((offer) => ({
                    draft_id: offer.draft_id,
                    product_id: offer.product_id,
                    price: offer.price,
                    amount: offer.amount,
                    property_set: offer.property_set,
                    photo_blob_ids: offer.photos.map(
                        (photo) => photo.signed_id,
                    ),
                    universe_id: offer.universe_id,
                    product_type_id: offer.product_type_id,
                    source: offer.source,
                    source_code: offer.source_code,
                    import_name: offer.import_name,
                    import_error: offer.import_error,
                })),
            },
            {
                onSuccess: () => {
                    createdSuccessfully = true;
                    setQueuedOffers([]);
                    setBulkPhotoUploadsInProgress(new Set());
                    resetEditor();
                },
                onError: (pageErrors) => {
                    setBulkErrors(pageErrors as OfferDraftErrors);
                },
                onFinish: () => {
                    setBulkProcessing(false);
                    if (!createdSuccessfully) resumeDraftSaving();
                },
            },
        );
    };

    const hasInvalidQueuedOffers = queuedOffers.some(
        (draft) => !isOfferDraftValid(draft, offerProperties),
    );
    const editingQueuedDraft =
        queuedOffers.find((draft) => draft.draft_id === editingDraftId) ?? null;
    const showAutoPriceToggle = isAutoPriceUniverseId(selectedUniverseId);
    const autoPriceSourceToggle = isMtgUniverseId(selectedUniverseId) ? (
        <Tabs
            value={autoPriceCurrency}
            onValueChange={(value) =>
                handleAutoPriceCurrencyChange(value as AutoPriceCurrency)
            }
            aria-label="Джерело автоцін"
        >
            <TabsList className="grid h-9 w-full grid-cols-2 overflow-hidden p-0.5 sm:w-[112px]">
                <TabsTrigger
                    value="EUR"
                    className="h-8 rounded-lg px-2 text-sm data-[state=active]:shadow-none"
                    aria-label="Cardmarket"
                    title="Cardmarket"
                >
                    <AutoPriceSourceLogo src={cardmarketLogoUrl} />
                </TabsTrigger>
                <TabsTrigger
                    value="USD"
                    className="h-8 rounded-lg px-2 text-sm data-[state=active]:shadow-none"
                    aria-label="TCGplayer"
                    title="TCGplayer"
                >
                    <AutoPriceSourceLogo src={tcgplayerLogoUrl} />
                </TabsTrigger>
            </TabsList>
        </Tabs>
    ) : (
        <div
            className="inline-flex h-9 items-center justify-center rounded-xl border border-input bg-background px-3"
            aria-label="Cardmarket"
            title="Cardmarket"
        >
            <AutoPriceSourceLogo src={cardmarketLogoUrl} />
        </div>
    );
    const trackedAutoPriceDrafts = queuedOffers.filter(
        (draft) => draft.auto_price_status !== null,
    );
    const autoPriceProgress = {
        total: trackedAutoPriceDrafts.length,
        completed: trackedAutoPriceDrafts.filter(
            (draft) =>
                draft.auto_price_status !== 'pending' &&
                draft.auto_price_status !== 'loading',
        ).length,
        queued: trackedAutoPriceDrafts.filter(
            (draft) => draft.auto_price_status === 'pending',
        ).length,
        loading: trackedAutoPriceDrafts.filter(
            (draft) => draft.auto_price_status === 'loading',
        ).length,
        priced: trackedAutoPriceDrafts.filter(
            (draft) => draft.auto_price_status === 'priced',
        ).length,
        manual: trackedAutoPriceDrafts.filter(
            (draft) => draft.auto_price_status === 'manual',
        ).length,
        unavailable: trackedAutoPriceDrafts.filter(
            (draft) => draft.auto_price_status === 'unavailable',
        ).length,
        errors: trackedAutoPriceDrafts.filter(
            (draft) => draft.auto_price_status === 'error',
        ).length,
    };
    const bulkAutoPriceControls: BulkAutoPriceControls | null =
        showAutoPriceToggle
            ? {
                  enabled: autoFillPrices,
                  onToggle: handleAutoPriceToggle,
                  adjustment: autoPriceMarkup,
                  onAdjustmentChange: handleAutoPriceMarkupChange,
                  adjustmentMode: autoPriceAdjustmentMode,
                  onAdjustmentModeChange: handleAutoPriceAdjustmentModeChange,
                  applyToManual: autoPriceApplyToManual,
                  onApplyToManualChange: handleAutoPriceApplyToManualChange,
                  exchangeRate: autoPriceExchangeRate,
                  sourceToggle: autoPriceSourceToggle,
                  progress: autoPriceProgress,
                  onRetryFailed: retryFailedAutoPrices,
              }
            : null;
    const headerActions = (
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-end">
            {draftSaveStatus !== 'idle' && (
                <span
                    className={
                        draftSaveStatus === 'error'
                            ? 'text-sm text-destructive'
                            : 'text-sm text-muted-foreground'
                    }
                    data-testid="offer-draft-save-status"
                    role={draftSaveStatus === 'error' ? 'alert' : 'status'}
                >
                    {draftSaveStatus === 'saving' && 'Збереження чернетки…'}
                    {draftSaveStatus === 'saved' && 'Чернетку збережено'}
                    {draftSaveStatus === 'error' &&
                        'Не вдалося зберегти чернетку'}
                </span>
            )}
            <Button
                type="button"
                variant="outlinePrimary"
                size="sm"
                onClick={() => setShowImportModal(true)}
            >
                Імпорт списком
            </Button>
        </div>
    );

    return (
        <div className="grid grid-cols-1 gap-6 sm:gap-8">
            <BulkOffersQueue
                queuedOffers={queuedOffers}
                offerProperties={offerProperties}
                editorProperties={properties}
                contextControls={
                    <div className="space-y-3">
                        <ProductContextSelectors
                            universes={universes}
                            selectedUniverseId={selectedUniverseId}
                            onUniverseChange={handleUniverseChange}
                            productTypes={productTypes}
                            selectedProductTypeId={selectedProductTypeId}
                            onProductTypeChange={handleProductTypeChange}
                            panel
                        />
                    </div>
                }
                headerExtra={headerActions}
                autoPriceControls={bulkAutoPriceControls}
                productSearch={
                    <BulkOfferProductSearch
                        selectedProduct={selectedProduct}
                        selectedUniverseId={selectedUniverseId}
                        selectedProductTypeId={selectedProductTypeId}
                        initialProductOptions={initialProductOptions}
                        productSearchSeed={
                            editingQueuedDraft?.source_code ?? null
                        }
                        onProductSelected={handleBulkProductSelected}
                        errors={bulkErrors}
                        editing={Boolean(editingQueuedDraft)}
                        onCancel={resetEditor}
                    />
                }
                processing={bulkProcessing}
                submitDisabled={
                    hasInvalidQueuedOffers ||
                    bulkPhotoUploadsInProgress.size > 0
                }
                onRemove={removeDraft}
                onDuplicate={duplicateDraft}
                onPhotoUploadingChange={handleBulkPhotoUploadingChange}
                onFieldChange={updateQueuedDraftField}
                onBulkRemove={removeDrafts}
                onBulkPropertyApply={applyPropertyToDrafts}
                onBulkManualPriceChange={updateBulkManualPrice}
                onBulkPriceApply={applyPriceToDrafts}
                onSubmit={submitBulkOffers}
            />

            <EmailConfirmationModal
                show={showConfirmationModal}
                onClose={() => setShowConfirmationModal(false)}
            />
            <OfferImportModal
                open={showImportModal}
                onOpenChange={setShowImportModal}
                universes={universes}
                defaultUniverseId={selectedUniverseId}
                currentImportBatchId={draftBatchId}
                existingDrafts={queuedOffers}
                autoPrice={showAutoPriceToggle && autoFillPrices}
                autoPriceCurrency={autoPriceCurrency}
                autoPriceMarkup={autoPriceMarkup}
                autoPriceAdjustmentMode={autoPriceAdjustmentMode}
                autoPriceApplyToManual={autoPriceApplyToManual}
            />
        </div>
    );
};
